import { useMemo } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { useWorkItems } from '../queries';
import { workItemRef } from '../paths';
import type { Project, WorkItem } from '../types';
import { useSelectedWorkItem } from '../useSelectedWorkItem';
import { filterParams, type WorkItemFilters } from './workItemFilters';
import { WorkItemDetailDrawer } from './WorkItemDetailDrawer';

const DAY = 86_400_000;
const parse = (s: string | null) => (s ? new Date(s).getTime() : null);

/** Simple timeline: a bar per scheduled work item (start_date → due_date). */
export function WorkItemsGantt({ project, filters }: { project: Project; filters?: WorkItemFilters }) {
  const itemsQuery = useWorkItems({ project: project.id, page_size: 500, ...filterParams(filters) });
  const [selected, setSelected] = useSelectedWorkItem();

  const { scheduled, min, span } = useMemo(() => {
    const items = (itemsQuery.data?.results ?? []).filter((i) => i.start_date || i.due_date);
    const stamps: number[] = [];
    for (const i of items) {
      const s = parse(i.start_date);
      const d = parse(i.due_date);
      if (s) stamps.push(s);
      if (d) stamps.push(d);
    }
    // Fallbacks are unused (we early-return when there are no scheduled items),
    // so a constant keeps the memo pure (no Date.now during render).
    const lo = stamps.length ? Math.min(...stamps) : 0;
    const hi = stamps.length ? Math.max(...stamps) : lo + 30 * DAY;
    return { scheduled: items, min: lo, span: Math.max(hi - lo, DAY) };
  }, [itemsQuery.data]);

  if (itemsQuery.isPending) return <Skeleton className="h-64 w-full" />;
  if (scheduled.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-10 text-center">
        <p className="text-muted-foreground text-sm">No work items with start/due dates to chart.</p>
      </div>
    );
  }

  const pct = (t: number) => ((t - min) / span) * 100;

  return (
    <>
      <div className="space-y-1.5">
        {scheduled.map((item: WorkItem) => {
          const s = parse(item.start_date) ?? parse(item.due_date)!;
          const d = parse(item.due_date) ?? s;
          const left = pct(s);
          const width = Math.max(pct(d) - left, 1.5);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelected(workItemRef(item))}
              className="hover:bg-muted/50 grid w-full grid-cols-[14rem_1fr] items-center gap-3 rounded px-1 py-1 text-left"
            >
              <span className="flex items-center gap-2 truncate text-sm">
                <span className="text-muted-foreground font-mono text-xs">{item.identifier}</span>
                <span className="truncate">{item.title}</span>
              </span>
              <span className="bg-muted relative h-5 rounded">
                <span
                  className="bg-primary/70 absolute top-0 h-5 rounded"
                  style={{ left: `${left}%`, width: `${width}%` }}
                />
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-muted-foreground mt-2 text-xs">
        {new Date(min).toLocaleDateString()} → {new Date(min + span).toLocaleDateString()}
      </p>

      <WorkItemDetailDrawer project={project} workItemId={selected} onClose={() => setSelected(null)} />
    </>
  );
}
